/**
 * duration_dial_controller.js — the half-hour duration wheel of every scheduling modal.
 *
 * The one visit-length picker: the detail-page schedule modal's footer dropup
 * (components/modals/schedule_event_modal.html) and both booking modals, the
 * calendar's quick-schedule modal and the Dispatch board's Book a visit
 * (macros/booking_fields.html). Options run 30 minutes to 10 hours in
 * half-hour steps; scroll to spin, tap to pick.
 *
 * The minutes live in the `input` target, which each modal's own script reads
 * and writes. Picking writes it and fires a bubbling `change` once per option
 * crossed, not once per scroll frame, because listeners redraw the calendar
 * selection on it. A script that sets the input fires `change` too, so the
 * label follows. The wheel re-centres on the input each time its dropdown
 * opens, which also catches a value written without an event (the detail
 * modal's day-view drag).
 *
 * Targets: dial (the listbox), input (minutes), label (optional: the button
 * text. The detail modal has none, because its button also reads "All Day"
 * and "2d" and schedule_modal_controller.js owns that wording.)
 * The 40px item height is load-bearing: schedule_event_modal.css sets it.
 */

import { Controller } from "@hotwired/stimulus";

const ITEM_HEIGHT = 40;
const MIN_MINUTES = 30;
const MAX_MINUTES = 600;
const STEP_MINUTES = 30;

/** "30m", "1h", "1h 30m". */
export function formatDuration(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
}

function snap(minutes) {
    const stepped = Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;
    return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, stepped));
}

function offsetOf(minutes) {
    return ((minutes - MIN_MINUTES) / STEP_MINUTES) * ITEM_HEIGHT;
}

export default class extends Controller {
    static targets = ["dial", "input", "label"];

    connect() {
        this._buildOptions();
        this._onClick = (event) => this._pickItem(event);
        this._onScroll = () => this._apply(snap(MIN_MINUTES + Math.round(this.dialTarget.scrollTop / ITEM_HEIGHT) * STEP_MINUTES));
        this._onShown = () => this._centre();
        this._onChange = () => this._render();
        this.dialTarget.addEventListener("click", this._onClick);
        this.dialTarget.addEventListener("scroll", this._onScroll);
        this.element.addEventListener("shown.bs.dropdown", this._onShown);
        this.inputTarget.addEventListener("change", this._onChange);
        this._render();
    }

    disconnect() {
        this.dialTarget.removeEventListener("click", this._onClick);
        this.dialTarget.removeEventListener("scroll", this._onScroll);
        this.element.removeEventListener("shown.bs.dropdown", this._onShown);
        this.inputTarget.removeEventListener("change", this._onChange);
    }

    // Rebuilt rather than appended: a Turbo cache restore reconnects a dial
    // that already holds its options.
    _buildOptions() {
        const items = [];
        for (let minutes = MIN_MINUTES; minutes <= MAX_MINUTES; minutes += STEP_MINUTES) {
            const item = document.createElement("div");
            item.className = "duration-dial__item";
            item.setAttribute("role", "option");
            item.dataset.minutes = minutes;
            item.textContent = formatDuration(minutes);
            items.push(item);
        }
        this.dialTarget.replaceChildren(...items);
    }

    _pickItem(event) {
        const item = event.target.closest(".duration-dial__item");
        if (!item) return;
        const minutes = parseInt(item.dataset.minutes, 10);
        this.dialTarget.scrollTo({ top: offsetOf(minutes), behavior: "smooth" });
        this._apply(minutes);
    }

    _apply(minutes) {
        if (parseInt(this.inputTarget.value, 10) === minutes) return;
        this.inputTarget.value = minutes;
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // 'shown', not 'show': scrollTop does nothing while the menu is display:none.
    _centre() {
        this.dialTarget.scrollTop = offsetOf(this._minutes());
        this._render();
    }

    _render() {
        const minutes = this._minutes();
        this.dialTarget.querySelectorAll(".duration-dial__item").forEach((item) => {
            const active = parseInt(item.dataset.minutes, 10) === minutes;
            item.classList.toggle("is-active", active);
            item.setAttribute("aria-selected", active ? "true" : "false");
        });
        if (this.hasLabelTarget) this.labelTarget.textContent = formatDuration(parseInt(this.inputTarget.value, 10) || minutes);
    }

    _minutes() {
        return snap(parseInt(this.inputTarget.value, 10) || MIN_MINUTES);
    }
}
